/**
 * ScreenerQuestionnaire — member-facing / CHW-assisted item administration.
 *
 * - Loads the screener (items + per-item response options) for an administration.
 * - Renders items one page at a time; each item's options come from the item
 *   itself (single = radio, multi = checkboxes, scale = radio row).
 * - Autosaves answered items periodically via PUT /responses.
 * - On safety_triggered (interpersonal-safety positive from an autosave/submit),
 *   surfaces safety resources immediately.
 * - Submit -> POST /submit, then hands the results to the parent.
 *
 * Accessibility: each item is a labelled group; keyboard-navigable; progress is
 * announced via aria-live.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SafetyResources } from './NeedsSummary';
import {
  socialNeedsScreenerApi,
  type CreatedReferral,
  type InstrumentDetail,
  type ItemResponse,
  type ScreenerItem,
} from './types';

const ITEMS_PER_PAGE = 4;
const AUTOSAVE_INTERVAL_MS = 20_000;

// item_number -> single code | array of codes (multi) | a typed number (scale
// items that carry no options)
type AnswerMap = Record<number, string | string[]>;

/**
 * The three calls this renderer makes. Supplied rather than imported so a
 * second instrument can reuse the whole component against its OWN router —
 * the benefits intake lives in the same screening_* tables but is served from
 * /api/benefits, because the social-needs router is behind a feature flag
 * belonging to a different product.
 */
export interface ScreenerApi {
  getInstrument(code: string): Promise<InstrumentDetail>;
  saveResponses(
    administrationId: string,
    body: { responses: ItemResponse[]; completion_time_seconds?: number },
  ): Promise<{ safety_triggered?: boolean }>;
  submit(
    administrationId: string,
  ): Promise<{ safety_triggered?: boolean; referrals_created?: CreatedReferral[] }>;
}

export interface ScreenerCopy {
  intro?: string;
  submitLabel?: string;
  doneTitle?: string;
  doneBody?: string;
}

const DEFAULT_COPY: Required<ScreenerCopy> = {
  intro:
    'These questions help identify resources that may be helpful. You can skip any question you prefer not to answer.',
  submitLabel: 'Submit screening',
  doneTitle: 'Thank you — the screening was submitted.',
  doneBody: 'Your navigator can review the identified needs and connect you with resources.',
};

interface ScreenerQuestionnaireProps {
  administrationId: string;
  instrumentCode: string;
  /** Defaults to the social-needs-screener client. */
  api?: ScreenerApi;
  copy?: ScreenerCopy;
  /** Fires on every change, so a host can show derived state as she answers. */
  onAnswersChange?: (answers: AnswerMap) => void;
  /** Called after a successful submit (parent typically routes to the summary). */
  onSubmitted?: (administrationId: string, referrals?: CreatedReferral[]) => void;
}

/**
 * An item whose response_type is `scale` but whose option list is EMPTY is a
 * free numeric answer, not a pick-list. Rendering it as a radio group over zero
 * options draws nothing at all, which is how a question disappears from a form
 * without anyone noticing — the benefits intake's household-size and monthly-
 * income items are exactly this shape.
 */
function isFreeNumeric(item: ScreenerItem): boolean {
  return item.response_type === 'scale' && (item.response_options ?? []).length === 0;
}

function isAnswered(item: ScreenerItem, value: string | string[] | undefined): boolean {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return value !== '';
}

export const ScreenerQuestionnaire: React.FC<ScreenerQuestionnaireProps> = ({
  administrationId,
  instrumentCode,
  api,
  copy,
  onAnswersChange,
  onSubmitted,
}) => {
  const client: ScreenerApi = api ?? socialNeedsScreenerApi;
  const text = { ...DEFAULT_COPY, ...(copy ?? {}) };
  const [detail, setDetail] = useState<InstrumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [answers, setAnswers] = useState<AnswerMap>({});
  const [page, setPage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [safetyTriggered, setSafetyTriggered] = useState(false);

  const startedAtRef = useRef<number>(Date.now());
  const lastSavedRef = useRef<string>('');

  // ── Load screener ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    client
      .getInstrument(instrumentCode)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load screener.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentCode]);

  const sortedItems = useMemo(
    () => (detail?.items ?? []).slice().sort((a, b) => a.item_number - b.item_number),
    [detail],
  );

  const totalItems = sortedItems.length;
  const answeredCount = sortedItems.filter((it) => isAnswered(it, answers[it.item_number])).length;
  const progressPct = totalItems > 0 ? Math.round((answeredCount / totalItems) * 100) : 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const pageItems = sortedItems.slice(page * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE + ITEMS_PER_PAGE);
  const isLastPage = page >= totalPages - 1;
  const allAnswered = totalItems > 0 && answeredCount === totalItems;

  // ── Autosave ───────────────────────────────────────────────────
  const buildResponses = useCallback((): ItemResponse[] => {
    return Object.entries(answers)
      .filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v !== ''))
      .map(([itemNumber, v]) => ({ item_number: Number(itemNumber), raw_response: v }));
  }, [answers]);

  const persist = useCallback(async () => {
    if (submitted) return;
    const responses = buildResponses();
    if (responses.length === 0) return;
    const signature = JSON.stringify(responses);
    if (signature === lastSavedRef.current) return;
    setSaving(true);
    try {
      const res = await client.saveResponses(administrationId, {
        responses,
        completion_time_seconds: Math.round((Date.now() - startedAtRef.current) / 1000),
      });
      lastSavedRef.current = signature;
      if (res.safety_triggered) setSafetyTriggered(true);
    } catch {
      // Autosave failures are non-fatal; the next tick (or submit) retries.
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [administrationId, buildResponses, submitted]);

  useEffect(() => {
    if (submitted) return;
    const id = window.setInterval(() => void persist(), AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [persist, submitted]);

  useEffect(() => {
    onAnswersChange?.(answers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  const setSingle = useCallback((itemNumber: number, code: string) => {
    setAnswers((prev) => ({ ...prev, [itemNumber]: code }));
  }, []);

  const toggleMulti = useCallback((itemNumber: number, code: string, checked: boolean) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[itemNumber]) ? (prev[itemNumber] as string[]) : [];
      const next = checked ? [...new Set([...current, code])] : current.filter((c) => c !== code);
      return { ...prev, [itemNumber]: next };
    });
  }, []);

  const goToPage = useCallback(
    (next: number) => {
      void persist();
      setPage(Math.max(0, Math.min(totalPages - 1, next)));
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [persist, totalPages],
  );

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      await persist();
      const res = await client.submit(administrationId);
      if (res.safety_triggered) setSafetyTriggered(true);
      setSubmitted(true);
      onSubmitted?.(administrationId, res.referrals_created);
    } catch {
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [administrationId, onSubmitted, persist]);

  // ── Render ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        Loading screener…
      </div>
    );
  }

  if (loadError || !detail) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {loadError ?? 'Screener unavailable.'}
        </CardContent>
      </Card>
    );
  }

  if (submitted) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {safetyTriggered && <SafetyResources />}
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <CheckCircle2 className="h-10 w-10 text-green-600" aria-hidden="true" />
            <h2 className="text-lg font-semibold">{text.doneTitle}</h2>
            <p className="max-w-md text-sm text-muted-foreground">{text.doneBody}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {safetyTriggered && <SafetyResources />}

      {/* Header + progress */}
      <Card>
        <CardHeader>
          <CardTitle>{detail.instrument.name}</CardTitle>
          <p className="text-sm text-muted-foreground">{text.intro}</p>
        </CardHeader>
        <CardContent>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span aria-live="polite">
              {answeredCount} of {totalItems} answered
            </span>
            <span className="text-muted-foreground">
              {saving ? 'Saving…' : 'Answers save automatically'}
            </span>
          </div>
          <Progress value={progressPct} aria-label={`${progressPct}% complete`} />
          <p className="mt-1 text-xs text-muted-foreground">
            Page {page + 1} of {totalPages}
          </p>
        </CardContent>
      </Card>

      {/* Items on the current page */}
      {pageItems.map((item) => (
        <Card key={item.item_number}>
          <CardContent className="py-5">
            <fieldset>
              <legend className="mb-1 text-base font-medium text-foreground">
                <span className="mr-2 text-muted-foreground">{item.item_number}.</span>
                {item.prompt}
              </legend>
              {item.help_text && (
                <p className="mb-3 text-sm text-muted-foreground">{item.help_text}</p>
              )}

              {isFreeNumeric(item) ? (
                <Input
                  id={`item-${item.item_number}`}
                  type="text"
                  inputMode="decimal"
                  className="max-w-xs"
                  value={(answers[item.item_number] as string) ?? ''}
                  onChange={(e) => setSingle(item.item_number, e.target.value)}
                  aria-label={item.prompt}
                />
              ) : item.response_type === 'multi' ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  {item.response_options.map((opt) => {
                    const id = `item-${item.item_number}-${opt.code}`;
                    const selected =
                      Array.isArray(answers[item.item_number]) &&
                      (answers[item.item_number] as string[]).includes(opt.code);
                    return (
                      <div
                        key={opt.code}
                        className={`flex items-center gap-3 rounded-md border p-3 transition-colors ${
                          selected ? 'border-blue-500 bg-blue-50' : 'hover:bg-muted/50'
                        }`}
                      >
                        <Checkbox
                          id={id}
                          checked={selected}
                          onCheckedChange={(c) =>
                            toggleMulti(item.item_number, opt.code, c === true)
                          }
                        />
                        <Label htmlFor={id} className="flex-1 cursor-pointer font-normal">
                          {opt.label}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <RadioGroup
                  value={(answers[item.item_number] as string) ?? ''}
                  onValueChange={(code) => setSingle(item.item_number, code)}
                  className={item.response_type === 'scale' ? 'grid gap-2' : 'grid gap-2 sm:grid-cols-2'}
                >
                  {item.response_options.map((opt) => {
                    const id = `item-${item.item_number}-${opt.code}`;
                    const selected = answers[item.item_number] === opt.code;
                    return (
                      <div
                        key={opt.code}
                        className={`flex items-center gap-3 rounded-md border p-3 transition-colors ${
                          selected ? 'border-blue-500 bg-blue-50' : 'hover:bg-muted/50'
                        }`}
                      >
                        <RadioGroupItem value={opt.code} id={id} />
                        <Label htmlFor={id} className="flex-1 cursor-pointer font-normal">
                          {opt.label}
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>
              )}
            </fieldset>
          </CardContent>
        </Card>
      ))}

      {/* Navigation */}
      <div className="flex items-center justify-between gap-3">
        <Button variant="outline" onClick={() => goToPage(page - 1)} disabled={page === 0}>
          <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          Back
        </Button>

        {isLastPage ? (
          <Button onClick={() => void handleSubmit()} disabled={submitting || answeredCount === 0}>
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {text.submitLabel}
          </Button>
        ) : (
          <Button onClick={() => goToPage(page + 1)}>
            Next
            <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>

      {!allAnswered && isLastPage && (
        <p className="text-center text-xs text-muted-foreground">
          You can submit with the questions you have answered so far.
        </p>
      )}
    </div>
  );
};
